import { useT } from "../../i18n/hooks.ts";
import {
  sourceRanges,
  WINDOW_SPACING_MS,
  type StationSelectionMethod,
} from "../../model/aemet/aemet.ts";
import { ROCIO_COVERAGE_END, ROCIO_COVERAGE_START } from "../../model/rocio.ts";
import type { AemetFetchState } from "./useAemetFetch.ts";
import { AemetAttributionNotice } from "./AemetAttributionNotice.tsx";
import { AemetDownloadProgress } from "./AemetDownloadProgress.tsx";
import { Dialog } from "../Dialog.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { InlineProgress } from "../InlineProgress.tsx";
import { SourceCoverageDetail } from "../SourceCoverageDetail.tsx";

/**
 * "Fetch from AEMET" for one sub-basin's precipitation/temperature (real)
 * and ETP (derived via Hargreaves, aemet.ts) — an alternative to uploading a
 * CSV or pasting values by hand (EditSubBasinDialog.tsx). Talks to our own
 * `/api/aemet/*` proxy (functions/api/aemet/), never to AEMET directly — see
 * aemet.ts's own header comment.
 *
 * Sequenced on purpose: candidate stations are shown BEFORE asking for a
 * date range (so the user knows what's about to be used before committing
 * to a download that can take minutes), and the coverage report is shown
 * BEFORE anything is written to the project (Apply is a separate, explicit
 * step).
 *
 * A pure view over `state` (useAemetFetch.ts, owned by EditSubBasinDialog.tsx)
 * — closing this dialog (`onClose`) only hides it, it does NOT cancel a
 * download in progress: the whole point of lifting the state up is that the
 * download keeps going in the background, and reopening this same dialog
 * (the same "Fetch from AEMET" button) shows it exactly where it left off,
 * with the "Cancel" button below acting as the actual stop control.
 */
export function AemetFetchDialog({
  state,
  onClose,
}: {
  state: AemetFetchState;
  onClose: () => void;
}) {
  const t = useT();
  const {
    phase,
    candidates,
    laneCount,
    errorMessage,
    forcedRange,
    freeStart,
    setFreeStart,
    freeEnd,
    setFreeEnd,
    method,
    setMethod,
    validRange,
    windowCount,
    targetDates,
    downloadRange,
    downloadDone,
    downloadTotal,
    downloadResult,
    retrying,
    applyErrorMessage,
    startDownload,
    cancelDownload,
    retryApply,
    retryFailedWindows,
    stationLabel,
    precipBuilt,
    tempBuilt,
    etoBuilt,
  } = state;

  return (
    <Dialog title={t("aemetDialog.title")} onClose={onClose}>
      <AemetAttributionNotice />

      {phase === "pending" && <p>{t("aemetDialog.pending")}</p>}

      {phase === "loading-stations" && (
        <InlineProgress
          text={t(
            method === "rocio" ? "aemetDialog.searchingCells" : "aemetDialog.searchingStations"
          )}
        />
      )}

      {phase === "stations-error" && (
        <p className="error">{t("aemetDialog.stationsError", { error: errorMessage })}</p>
      )}

      {phase === "download-error" && (
        <p className="error">{t("aemetDialog.downloadError", { error: errorMessage })}</p>
      )}

      {phase === "no-stations" && (
        <p className="error">
          {t(method === "rocio" ? "aemetDialog.noRocioCoverage" : "aemetDialog.noStations")}
        </p>
      )}

      {(phase === "range" || phase === "downloading") && candidates.length > 0 && (
        <>
          {method === "rocio" ? (
            <p>
              {t(
                candidates.length === 1
                  ? "aemetDialog.rocioCellsOne"
                  : "aemetDialog.rocioCellsMany",
                { n: String(candidates.length) }
              )}
            </p>
          ) : (
            <>
              <p>
                {t("aemetDialog.mainStation", {
                  name: candidates[0].name,
                  km: candidates[0].distanceKm.toFixed(1),
                  elevation: String(candidates[0].altitudeM),
                })}
              </p>
              {candidates.length > 1 && (
                <p className="muted-text">
                  {t("aemetDialog.backupStations", {
                    list: candidates
                      .slice(1)
                      .map((c) => `${c.name} (${c.distanceKm.toFixed(1)} km)`)
                      .join(", "),
                  })}
                </p>
              )}
            </>
          )}

          <h4>
            {t("aemetDialog.rangeTitle")}{" "}
            {method === "rocio" && <InfoIcon text={t("aemetDialog.rocioRangeLimit")} />}
          </h4>
          {forcedRange ? (
            <p className="muted-text">
              {t("aemetDialog.rangeFixedNotice", {
                from: forcedRange.start,
                to: forcedRange.end,
              })}
            </p>
          ) : (
            <>
              <div className="assigner-controls">
                <label>{t("aemetDialog.startDate")}</label>
                <input
                  type="date"
                  value={freeStart}
                  min={method === "rocio" ? ROCIO_COVERAGE_START : undefined}
                  max={method === "rocio" ? ROCIO_COVERAGE_END : undefined}
                  disabled={phase === "downloading"}
                  onChange={(e) => setFreeStart(e.target.value)}
                />
              </div>
              <div className="assigner-controls">
                <label>{t("aemetDialog.endDate")}</label>
                <input
                  type="date"
                  value={freeEnd}
                  min={method === "rocio" ? ROCIO_COVERAGE_START : undefined}
                  max={method === "rocio" ? ROCIO_COVERAGE_END : undefined}
                  disabled={phase === "downloading"}
                  onChange={(e) => setFreeEnd(e.target.value)}
                />
              </div>
            </>
          )}

          {!validRange && <p className="error">{t("aemetDialog.invalidRange")}</p>}

          <h4>{t("aemetDialog.methodTitle")}</h4>
          <div className="assigner-controls">
            <label>{t("aemetDialog.methodTitle")}</label>
            <select
              value={method}
              disabled={phase === "downloading"}
              onChange={(e) => setMethod(e.target.value as StationSelectionMethod)}
            >
              <option value="nearest">{t("aemetDialog.methodNearest")}</option>
              <option value="idw">{t("aemetDialog.methodIdw")}</option>
              <option value="rocio">{t("aemetDialog.methodRocio")}</option>
            </select>
          </div>
          <p className="muted-text">
            {t(
              method === "idw"
                ? "aemetDialog.methodIdwHelp"
                : method === "rocio"
                  ? "aemetDialog.methodRocioHelp"
                  : "aemetDialog.methodNearestHelp"
            )}
          </p>

          {phase === "range" && validRange && (
            <>
              {method !== "rocio" && (
                // ROCIO is always a single R2-backed request regardless of the
                // date range (see fetchRocioValues in rocio.ts) — no windowing/
                // pacing applies, so there's nothing meaningful to estimate.
                <p className="muted-text">
                  {(() => {
                    const estimatedMinutes = Math.max(
                      1,
                      Math.round((Math.ceil(windowCount / laneCount) * WINDOW_SPACING_MS) / 60_000)
                    );
                    return t(
                      estimatedMinutes === 1
                        ? "aemetDialog.estimateOneMinute"
                        : "aemetDialog.estimateMinutes",
                      { min: String(estimatedMinutes) }
                    );
                  })()}
                </p>
              )}
              <div className="dialog-actions">
                <button className="button button-primary" onClick={startDownload}>
                  {t("aemetDialog.startDownload")}
                </button>
                <button className="button" onClick={onClose}>
                  {t("common.cancel")}
                </button>
              </div>
            </>
          )}

          {phase === "downloading" && (
            <>
              <AemetDownloadProgress
                done={downloadDone}
                total={downloadTotal}
                range={downloadRange}
              />
              <div className="dialog-actions">
                <button className="button" onClick={cancelDownload}>
                  {t("common.cancel")}
                </button>
                <InfoIcon text={t("aemetDialog.continuesInBackground")} />
              </div>
            </>
          )}
        </>
      )}

      {phase === "report" && precipBuilt && tempBuilt && etoBuilt && downloadResult && (
        <>
          <h4>{t("aemetDialog.reportTitle")}</h4>
          {!applyErrorMessage && <p>{t("aemetDialog.appliedAutomatically")}</p>}
          {downloadResult.failedWindows.length > 0 && (
            <div className="aemet-failed-windows">
              <p className="error">
                {t("aemetDialog.failedWindows", {
                  n: String(downloadResult.failedWindows.length),
                })}
              </p>
              <ul className="zones-list">
                {downloadResult.failedWindows.map((w) => (
                  <li key={`${w.start}_${w.end}`}>
                    {t("aemetDialog.failedWindowRange", { from: w.start, to: w.end })}
                  </li>
                ))}
              </ul>
              {retrying ? (
                <>
                  <InlineProgress
                    text={t("aemetDialog.retrying", {
                      done: String(downloadDone),
                      total: String(downloadTotal),
                    })}
                  />
                  <button className="button" onClick={cancelDownload}>
                    {t("common.cancel")}
                  </button>
                </>
              ) : (
                <button className="button" onClick={retryFailedWindows}>
                  {t("aemetDialog.retryFailedWindows", {
                    n: String(downloadResult.failedWindows.length),
                  })}
                </button>
              )}
            </div>
          )}
          <div>
            <h4>{t("infoPopover.precipitation")}</h4>
            <SourceCoverageDetail
              bySource={sourceRanges(precipBuilt.sourceByDate, targetDates)}
              stationName={stationLabel}
            />
          </div>
          <div>
            <h4>{t("infoPopover.temperature")}</h4>
            <SourceCoverageDetail
              bySource={sourceRanges(tempBuilt.sourceByDate, targetDates)}
              stationName={stationLabel}
            />
          </div>
          <div>
            <h4>{t("infoPopover.etp")}</h4>
            <SourceCoverageDetail
              bySource={sourceRanges(etoBuilt.sourceByDate, targetDates)}
              stationName={stationLabel}
            />
          </div>
          {applyErrorMessage && <p className="error">{applyErrorMessage}</p>}
          <div className="dialog-actions">
            {applyErrorMessage && (
              <button className="button button-primary" onClick={retryApply}>
                {t("aemetDialog.retryApply")}
              </button>
            )}
            <button className="button" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
